import React, { useState, useEffect } from 'react';
import { getProducts, createProduct, updateProduct, deleteProduct, uploadImage, getAddons } from '../../services/api';
import { Product, Addon } from '../../types';
import { PackageIcon, EditIcon, TrashIcon, PlusIcon, ChevronDownIcon, ChevronRightIcon, GripVerticalIcon } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// SortableImage component
interface SortableImageProps {
  id: string;
  index: number;
  imageUrl: string;
  onRemove: (index: number) => void;
}

const SortableImage: React.FC<SortableImageProps> = ({ id, index, imageUrl, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group"
    >
      <div
        {...attributes}
        {...listeners}
        className="absolute top-0 left-0 z-10 bg-blue-500 bg-opacity-80 rounded-br-lg p-2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing hover:bg-opacity-100"
        title="Drag to reorder"
        style={{ minWidth: '32px', minHeight: '32px' }}
      >
        <GripVerticalIcon className="h-5 w-5 text-white" />
      </div>
      <img
        src={imageUrl}
        alt={`Product ${index + 1}`}
        className="h-20 w-20 object-cover rounded-lg border border-gray-200"
      />
      <button
        type="button"
        onClick={() => onRemove(index)}
        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Remove image"
      >
        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div className="absolute bottom-1 left-1 bg-black bg-opacity-70 text-white text-xs rounded px-1 py-0.5">
        {index + 1}
      </div>
    </div>
  );
};

const ProductsManagement: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_per_day: '',
    images: [] as string[],
    linked_addons: [] as string[],
    display_order: '0'
  });
  const [addons, setAddons] = useState<Addon[]>([]);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Setup sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchProducts();
    fetchAddons();
  }, []);

  const fetchProducts = async () => {
    try {
      const data = await getProducts();
      setProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAddons = async () => {
    try {
      const data = await getAddons();
      setAddons(data);
    } catch (error) {
      console.error('Error fetching addons:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const productData = {
        product_id: editingProduct ? editingProduct.product_id : formData.name.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-') + '-' + Date.now(),
        name: formData.name,
        description: formData.description,
        price: {
          currency: "USD",
          amount_cents: Math.round(parseFloat(formData.price_per_day) * 100)
        },
        images: editingProduct 
          ? formData.images.length > 0 ? formData.images : editingProduct.images
          : formData.images,
        linked_addons: formData.linked_addons,
        display_order: parseInt(formData.display_order) || 0
      };

      if (editingProduct) {
        await updateProduct(editingProduct.product_id, productData);
      } else {
        await createProduct(productData);
      }

      fetchProducts();
      setShowForm(false);
      setEditingProduct(null);
      setFormData({ name: '', description: '', price_per_day: '', images: [], linked_addons: [], display_order: '0' });
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price_per_day: product.price ? (product.price.amount_cents / 100).toString() : '0',
      images: product.images || [],
      linked_addons: product.linked_addons || [],
      display_order: (product.display_order ?? 0).toString()
    });
    setShowForm(true);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingImage(true);
    try {
      // Generate a temporary product ID for uploads, will be updated when product is saved
      const tempProductId = editingProduct 
        ? editingProduct.product_id 
        : formData.name.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-') + '-' + Date.now();
      
      const uploadPromises = Array.from(files).map(file => 
        uploadImage(file, tempProductId, 'product-image', `Product image for ${formData.name}`)
      );
      const results = await Promise.all(uploadPromises);
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...results.map(r => r.s3_url)]
      }));
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Failed to upload images');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleImageReorder = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = formData.images.findIndex((_, index) => `image-${index}` === active.id);
      const newIndex = formData.images.findIndex((_, index) => `image-${index}` === over?.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        setFormData(prev => ({
          ...prev,
          images: arrayMove(prev.images, oldIndex, newIndex)
        }));
      }
    }
  };

  const handleDelete = async (productId: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProduct(productId);
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingProduct(null);
    setFormData({ name: '', description: '', price_per_day: '', images: [], linked_addons: [], display_order: '0' });
  };

  const toggleRowExpanded = (productId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <h3 className="text-lg font-medium text-gray-900">Products Management</h3>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Product
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {!editingProduct && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product ID
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-') + '-' + Date.now()}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
                    placeholder="Auto-generated from name"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price per Day
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.price_per_day}
                  onChange={(e) => setFormData({ ...formData, price_per_day: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">Lower numbers appear first on the rentals page</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                required
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter product description. Press Enter for new lines."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Linked Addons
              </label>
              <div className="space-y-2">
                {addons.map((addon) => (
                  <label key={addon.addon_id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.linked_addons.includes(addon.addon_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ 
                            ...formData, 
                            linked_addons: [...formData.linked_addons, addon.addon_id] 
                          });
                        } else {
                          setFormData({ 
                            ...formData, 
                            linked_addons: formData.linked_addons.filter(id => id !== addon.addon_id) 
                          });
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{addon.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Images
              </label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">Upload image(s):</label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
                    />
                  </div>
                  {uploadingImage && (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-sm text-gray-600">Uploading...</span>
                    </div>
                  )}
                </div>
                
                {formData.images.length > 0 && (
                  <div className="mt-4">
                    <label className="block text-xs text-gray-600 mb-2">
                      Current images (drag to reorder):
                    </label>
                    <div className="text-xs text-gray-500 mb-2 flex items-center">
                      <GripVerticalIcon className="h-3 w-3 mr-1" />
                      Drag images by the grip handle to change their display order
                    </div>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleImageReorder}
                    >
                      <SortableContext
                        items={formData.images.map((_, index) => `image-${index}`)}
                        strategy={horizontalListSortingStrategy}
                      >
                        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                          {formData.images.map((imageUrl, index) => (
                            <SortableImage
                              key={`image-${index}`}
                              id={`image-${index}`}
                              index={index}
                              imageUrl={imageUrl}
                              onRemove={handleRemoveImage}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>
                )}
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                {editingProduct ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {Array.isArray(products) && products.map((product) => (
          <div key={product.product_id} className="bg-white rounded-lg shadow p-4">
            <div className="flex items-start gap-3 mb-3">
              <div className="flex-shrink-0">
                {product.images && product.images.length > 0 ? (
                  <div className="relative">
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="h-16 w-16 rounded-lg object-cover"
                    />
                    {product.images.length > 1 && (
                      <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        +{product.images.length - 1}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="h-16 w-16 bg-gray-200 rounded-lg flex items-center justify-center">
                    <PackageIcon className="h-8 w-8 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-gray-900 truncate">{product.name || 'Unknown Product'}</h4>
                <p className="text-xs text-gray-500 mt-0.5">
                  ${product.price ? (product.price.amount_cents / 100).toFixed(2) : '0.00'} / day
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {product.linked_addons ? product.linked_addons.length : 0} addon{(product.linked_addons?.length || 0) !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
            
            {product.description && (
              <p className="text-sm text-gray-600 line-clamp-2 mb-3">{product.description}</p>
            )}
            
            <div className="flex items-center justify-between pt-3 border-t border-gray-200">
              <button
                onClick={() => toggleRowExpanded(product.product_id)}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center"
              >
                {expandedRows.has(product.product_id) ? (
                  <>
                    <ChevronDownIcon className="h-4 w-4 mr-1" />
                    Hide Details
                  </>
                ) : (
                  <>
                    <ChevronRightIcon className="h-4 w-4 mr-1" />
                    View Details
                  </>
                )}
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(product)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                  title="Edit"
                >
                  <EditIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(product.product_id)}
                  className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded"
                  title="Delete"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            {expandedRows.has(product.product_id) && (
              <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                {product.images && product.images.length > 0 && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 mb-2">Images ({product.images.length})</h5>
                    <div className="grid grid-cols-4 gap-2">
                      {product.images.map((img, idx) => (
                        <img key={idx} src={img} alt={`${product.name} ${idx + 1}`} className="h-16 w-16 object-cover rounded-lg border" />
                      ))}
                    </div>
                  </div>
                )}
                {product.linked_addons && product.linked_addons.length > 0 && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 mb-2">Linked Addons</h5>
                    <div className="flex flex-wrap gap-1">
                      {product.linked_addons.map((addonId) => {
                        const addon = addons.find(a => a.addon_id === addonId);
                        return (
                          <span key={addonId} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {addon?.name || addonId}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div>
                  <h5 className="text-xs font-semibold text-gray-700 mb-1">Product ID</h5>
                  <p className="text-xs text-gray-500 font-mono break-all">{product.product_id}</p>
                </div>
              </div>
            )}
          </div>
        ))}
        {products.length === 0 && (
          <div className="text-center py-12 text-gray-500 bg-white rounded-lg">
            No products found
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price/Day
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Linked Addons
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(products) && products.map((product) => (
                <React.Fragment key={product.product_id}>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        {product.images && product.images.length > 0 ? (
                          <div className="relative group">
                            <img
                              src={product.images[0]}
                              alt={product.name}
                              className="h-10 w-10 rounded-lg object-cover"
                            />
                            {product.images.length > 1 && (
                              <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                                +{product.images.length - 1}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="h-10 w-10 bg-gray-200 rounded-lg flex items-center justify-center">
                            <PackageIcon className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{product.name || 'Unknown Product'}</div>
                        {product.images && product.images.length > 1 && (
                          <div className="text-xs text-gray-500">{product.images.length} images</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 line-clamp-2 whitespace-pre-wrap">
                      {product.description || 'No description available'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${product.price ? (product.price.amount_cents / 100).toFixed(2) : '0.00'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {product.linked_addons ? product.linked_addons.length : 0} addon{(product.linked_addons?.length || 0) !== 1 ? 's' : ''}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => toggleRowExpanded(product.product_id)}
                        className="text-gray-600 hover:text-gray-900"
                        title="View Details"
                      >
                        {expandedRows.has(product.product_id) ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(product)}
                        className="text-gray-600 hover:text-gray-900"
                        title="Edit"
                      >
                        <EditIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(product.product_id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedRows.has(product.product_id) && (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 bg-gray-50">
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Full Description</h4>
                          <p className="text-sm text-gray-600 whitespace-pre-wrap">{product.description || 'No description available'}</p>
                        </div>
                        {product.images && product.images.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Images ({product.images.length})</h4>
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                              {product.images.map((img, idx) => (
                                <img key={idx} src={img} alt={`${product.name} ${idx + 1}`} className="h-20 w-20 object-cover rounded-lg border" />
                              ))}
                            </div>
                          </div>
                        )}
                        {product.linked_addons && product.linked_addons.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Linked Addons</h4>
                            <div className="flex flex-wrap gap-2">
                              {product.linked_addons.map((addonId) => {
                                const addon = addons.find(a => a.addon_id === addonId);
                                return (
                                  <span key={addonId} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {addon?.name || addonId}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-1">Product ID</h4>
                          <p className="text-xs text-gray-500 font-mono">{product.product_id}</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        
        {products.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No products found
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsManagement;
